import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { RbacFormField } from '../../../../shared/component/rbac-form/rbac-form-field.model';
import { RbacFormComponent } from '../../../../shared/component/rbac-form/rbac-form.component';
import { RbacModalComponent } from '../../../../shared/component/rbac-modal/rbac-modal.component';
import { RbacTableColumn } from '../../../../shared/component/rbac-table/rbac-table-column.model';
import { RbacTableComponent } from '../../../../shared/component/rbac-table/rbac-table.component';
import { InnerModule, ModalFieldDef, Module, SubModule, HIERARCHICAL_ROUTES, findRouteByPath } from '../rbac.models';
import { ApiCallsService } from '../../../../shared/services/api-calls.service';
import { ToastService } from '../../../../shared/services/toast.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-innermodule-management',
  standalone: true,
  imports: [CommonModule, FormsModule, RbacTableComponent, RbacFormComponent, RbacModalComponent],
  templateUrl: './innermodule-management.component.html',
  styleUrls: ['./innermodule-management.component.scss']
})
export class InnerModuleManagementComponent implements OnChanges {
  @Input({ required: true }) subModule!: SubModule;
  @Input({ required: true }) module!: Module;
  @Output() back = new EventEmitter<void>();

  innerModules: InnerModule[] = [];
  loading = false;
  saving = false;
  innerModuleModalOpen = false;
  modalOpen = false;
  editingInnerModule: InnerModule | null = null;

  /** Dynamic modal fields being edited for the inner module */
  editingModalFields: ModalFieldDef[] = [];

  columns: RbacTableColumn[] = [
    { key: 'name', label: 'Sub-Module Name' },
    { key: 'api_url', label: 'API URL' },
    { key: 'category', label: 'Category' },
    { key: 'order', label: 'Order', width: '80px' },
    { key: 'is_view', label: 'Read', type: 'badge-yn' },
    { key: 'is_add', label: 'Add', type: 'badge-yn' },
    { key: 'is_edit', label: 'Edit', type: 'badge-yn' },
    { key: 'sub_module', label: 'Has Inner-Modules', type: 'badge-yn' },
    { key: 'status', label: 'Status', type: 'status' }
  ];

  fields: RbacFormField[] = [];


  constructor(private configurationService: ApiCallsService,private toast:ToastService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['subModule'] && this.subModule) {
      this.buildFields();
      this.loadInnerModules();
      
    }
  }

  private buildFields(): void {
    const parentSubRoute = findRouteByPath(HIERARCHICAL_ROUTES, this.subModule?.url || '');
    const innerRouteOptions = parentSubRoute && parentSubRoute.children
      ? parentSubRoute.children.map(c => ({ label: c.label, value: c.path }))
      : [];

    this.fields = [
      { key: 'name', label: 'Sub-Module Name', type: 'text', required: true, placeholder: 'e.g. Daily Report' },
      { key: 'url', label: 'URL', type: 'select', placeholder: 'Select URL...', options: innerRouteOptions },
      { key: 'api_url', label: 'API URL', type: 'text', placeholder: '/Table-Configuration', default: '/Table-Configuration' },
      { key: 'order', label: 'Order', type: 'number', required: true },
      { key: 'parent_id', label: 'Module', type: 'hidden' },
      { key: 'is_view', label: 'Can View?', type: 'toggle' },
      { key: 'is_add', label: 'Can Add?', type: 'toggle' },
      { key: 'is_edit', label: 'Can Edit?', type: 'toggle' },
      { key: 'sub_module', label: 'Has Inner-Modules?', type: 'toggle' },
      { key: 'category', label: 'Category', type: 'select', placeholder: 'Select category types...', options: [] }
    ];
  }
  private loadCategories(): void {
  this.configurationService.getCategories().subscribe({
    next: (res) => {
      const categories = res?.Data ?? res ?? [];
      const categoryField = this.fields.find(f => f.key === 'category');
      if (categoryField) {
        categoryField.options = categories.map((c: any) => ({
          label: c.name,
          value: c.category_type 
        }));
        this.fields = [...this.fields]; // new array reference so the form re-renders if it's already open
      }
    }
  });
}

loadInnerModules(): void {
  this.loading = true;
  this.configurationService
    .get({ type: 'I', parent_id: this.subModule.id! })
    .pipe(finalize(() => (this.loading = false)))
    .subscribe({
      next: (res) => {
        const all = res?.Data ?? res ?? [];
        this.innerModules = all.filter((row: any) => row.type === 'I' && row.parent_id === this.subModule.id);
      },
      error: () => (this.innerModules = [])
    });
    this.loadCategories();
}
openAdd(): void {
  this.editingInnerModule = {
    parent_id: this.subModule.id!,
    sub_module_id: this.subModule.id!,
    module_id: this.module._id!
  } as InnerModule;
  this.editingModalFields = [{ id: '', placeholder: '' }];
  this.modalOpen = true;
}
  openEdit(innerModule: InnerModule): void {
    this.editingInnerModule = innerModule;
    this.editingModalFields = innerModule.modalFields
      ? JSON.parse(JSON.stringify(innerModule.modalFields))
      : [{ id: '', placeholder: '' }];
    this.modalOpen = true;
  }

  closeModal(): void {
    this.modalOpen = false;
    this.editingInnerModule = null;
    this.editingModalFields = [];
  }

  // ── Modal field builder helpers ──────────────────────────────

  addModalField(): void {
    this.editingModalFields.push({ id: '', placeholder: '' });
  }

  removeModalField(index: number): void {
    this.editingModalFields.splice(index, 1);
  }

  // ── Save ─────────────────────────────────────────────────────

onSave(value: Record<string, any>): void {
  this.saving = true;
  const { _id, ...rest } = value;
  const payload: any = {
    ...rest,
    order: Number(value['order']),
    type: 'I',
    parent_id: this.subModule.id,
    sub_module_id: this.subModule.id,
    module_id: this.module._id,
    modalFields: value['render_type'] === 'modal' ? this.editingModalFields : []
  };
  const action = _id ? 'Updated' : 'Added';

  const request$ = _id
    ? this.configurationService.update({ id: _id, ...payload })
    : this.configurationService.add(payload as InnerModule);

  request$.pipe(finalize(() => (this.saving = false))).subscribe({
    next: (res: any) => {
      if (res?.Status === 'Error' || res?.status === 'Error' || res?.Status === 'error' || res?.status === 'error') {
        const errorMsg = res?.Message || res?.message || `Failed to ${action.toLowerCase()} Inner Module`;
        this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
        return;
      }
      this.toast.showToast(`Inner Module ${action} Successfully`, 'colored', 5000, 'success');
      this.closeModal();
      this.loadInnerModules();
    },
    error: err => {
      const errorMsg = err?.error?.Message || err?.error?.message || err?.Message || err?.message || `Failed to ${action.toLowerCase()} Inner Module`;
      this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
    }
  });
}

onToggleStatus(innerModule: InnerModule): void {
  const id = innerModule.id || innerModule._id;
  if (!id) { return; }
  const nextStatus = innerModule.status === 'N' ? 'Y' : 'N';
  this.configurationService
    .update({ id, status: nextStatus, order: innerModule.order, type: 'I', parent_id: this.subModule.id })
    .subscribe({
      next: (res: any) => {
        if (res?.Status === 'Error' || res?.status === 'Error' || res?.Status === 'error' || res?.status === 'error') {
          const errorMsg = res?.Message || res?.message || 'Failed to update status';
          this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
          return;
        }
        this.loadInnerModules();
      },
      error: err => {
        const errorMsg = err?.error?.Message || err?.error?.message || err?.Message || err?.message || 'Failed to update status';
        this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
      }
    });
}

onDelete(innerModule: InnerModule): void {
  const id = innerModule.id || innerModule._id;
  if (!id) { return; }
  Swal.fire({
    title: 'Are you sure?',
    text: `Do you really want to delete inner module "${innerModule.name}"?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Yes, delete it',
    cancelButtonText: 'No, cancel',
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6'
  }).then((result) => {
    if (!result.isConfirmed) {
      this.toast.showToast('Action cancelled', 'colored', 3000, 'info');
      return;
    }
    this.configurationService
      .update({
        id,
        ...innerModule,
        status: 'N',
        order: Number(innerModule.order),
        type: 'I',
        parent_id: this.subModule.id
      })
      .subscribe({
        next: (res: any) => {
          if (res?.Status === 'Error' || res?.status === 'Error' || res?.Status === 'error' || res?.status === 'error') {
            const errorMsg = res?.Message || res?.message || 'Failed to delete inner module';
            this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
            return;
          }
          this.toast.showToast('Inner module deleted successfully', 'colored', 5000, 'success');
          this.loadInnerModules();
        },
        error: err => {
          const errorMsg = err?.error?.Message || err?.error?.message || err?.Message || err?.message || 'Failed to delete inner module';
          this.toast.showToast(errorMsg, 'colored', 5000, 'danger');
        }
      });
  });
}

get isModalType(): boolean {
  return this.editingInnerModule?.render_type === 'modal';
}

}
